// US-56 — глобальный React ErrorBoundary, оборачивает <App/>.
//
// Когда внутри дерева React выкидывается unhandled exception (например,
// `Cannot read property of undefined` после неожиданного формата ответа API),
// componentDidCatch заходит в maintenance-state с reason="crash". Overlay
// показывает «Что-то пошло не так» + кнопку «Обновить страницу». Auto-retry
// для крэшей не выполняется — нужно явное действие пользователя.

import { Component, type ErrorInfo, type ReactNode } from "react";
import { enterMaintenance } from "./state";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // В консоли — для отладки. В проде Sentry (US-28) подхватит, когда поставим.
    // eslint-disable-next-line no-console
    console.error("[US-56] ErrorBoundary caught:", error, info);
    enterMaintenance("crash");
  }

  render(): ReactNode {
    // Дерево скрывается, но overlay рендерится из <App>-уровня поверх всего.
    // Возвращаем пустой <div>, чтобы старое дерево с поломанным состоянием
    // не пыталось перерисовываться. MaintenanceOverlay рендерится отдельно
    // в main.tsx ВНЕ ErrorBoundary, поэтому он доступен.
    if (this.state.hasError) {
      return <div data-testid="error-boundary-fallback" />;
    }
    return this.props.children;
  }
}
